import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import useAppDispatch from "@/Hooks/useAppDispatch";
import { Link, useLocation } from "react-router-dom";
import { Layout, Menu } from "antd";
import classnames from "classnames";
import Icon, {
  DashboardOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  ControlOutlined,
  ApiOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  ProfileOutlined,
  FolderAddOutlined,
} from "@ant-design/icons";
import { useGeneralizer } from "@/components/Generalizer";
import { AppPathNames } from "@/constants/AppPathNames";
import { useGates } from "@/gates";
import * as permissions from "@/gates/permissions";
import { useWindowSize } from "@/Hooks/useWindowSize";
import { toggleSideBar } from "@/store/actions/settingsActions";
import { isSuperAdmin, isUiPathUser } from "@/utils/utils";
import { organizationsNames } from "@/container/Organizations/organizationsNames";
import { slices } from "@/constants/sliceNames";

import { MonitorIcon } from "../../svgs/MonitorIcon";

import "./Sidebar.scss";
import SubMenu from "antd/lib/menu/SubMenu";

const { Sider } = Layout;
const { Item } = Menu;

const orgMap = {
  [organizationsNames.hoya]: true,
  [organizationsNames.hoyaSidewall]: true,
  [organizationsNames.nagasaka]: true,
  [organizationsNames.averroes]: true,
  [organizationsNames.abi]: true,
  [organizationsNames.ZeitView]: true,
};

/**
 * TriggerCollapse ui component, used in side bar as
 * a ui for trigger that collapse it
 * @param {boolean} collapsed is side bar collapsed
 */
const TriggerCollapse = ({ collapsed }) => {
  return (
    <span className="sidebar__menu-item--collapse">
      {collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}

      <span
        className={classnames({
          "sidebar__menu-item--collapse-text-hiddin": collapsed,
          "sidebar__menu-item--collapse-text": !collapsed,
        })}
      >
        Close Menu
      </span>
    </span>
  );
};


/**
 * Main sidebar coomponent, used in side bar as
 * a ui for trigger that collapse it
 * @param {string} defaultSelected default selected menu
 */
const SideBar = ({ defaultSelected = "/" }) => {
  const globalCollapsed = useSelector((state) => state.antSettings.collapsed);
  const { userRole } = useSelector((state) => state[slices.auth]);

  const isConnectorsEnabled = useGeneralizer(orgMap, false);

  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { width } = useWindowSize();
  const { can, cannot, canAny } = useGates();

  const [collapsed, setCollapsed] = useState(globalCollapsed);
  const [selectedKey, setSelectedKey] = useState(pathname);

  const monitoringDropdownItems = useMemo(
    () => [
      {
        key: AppPathNames.monitoringQCBatchPrediction,
        text: "Batch Prediction",
      },
      {
        key: AppPathNames.monitoringQCRealtimeDatasources,
        text: "Data Source",
      },
      {
        key: AppPathNames.monitoringQCRealtimeCameras,
        text: "Camera",
      },
    ],
    []
  );
  /**
   * set selected menu item
   * when route change
   */
  useEffect(() => {
    if (pathname.includes(AppPathNames.datasets)) {
      setSelectedKey(AppPathNames.datasets);
    } else if (pathname.includes(AppPathNames.browseFiles)) {
      setSelectedKey(AppPathNames.browseFiles);
    } else if (pathname.includes(AppPathNames.monitoring)) {
      setSelectedKey(AppPathNames.monitoring);
    } else if (pathname.includes(AppPathNames.batchPredictions)) {
      setSelectedKey(AppPathNames.batchPredictions);
    } else if (pathname.includes(AppPathNames.machines)) {
      setSelectedKey(AppPathNames.machines);
    } else if (pathname.includes(AppPathNames.connectors)) {
      setSelectedKey(AppPathNames.connectors);
    } else {
      setSelectedKey(pathname);
    }
  }, [pathname]);

  useEffect(() => {
    if (width < 1000) setCollapsed(true);
    else setCollapsed(globalCollapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  /**
   * handle sidebar collapse change
   */
  const handleCollapse = useCallback(() => {
    dispatch(toggleSideBar(!collapsed));
    setCollapsed((collapsed) => !collapsed);
  }, [dispatch, collapsed]);

  return (
    <Sider
      className="sidebar"
      width={245}
      collapsedWidth={80}
      collapsed={collapsed}
      collapsible
      onCollapse={handleCollapse}
      trigger={<TriggerCollapse collapsed={collapsed} />}
    >
      <Menu
        mode="vertical"
        // inlineCollapsed={collapsed}
        defaultSelectedKeys={[defaultSelected]}
        selectedKeys={[selectedKey]}
        className="sidebar__menu"
      >
        <Item className="sidebar__menu-item" key={AppPathNames.dashboard}>
          <Link to={AppPathNames.dashboard}>
            <DashboardOutlined />
            <span>Dashboard</span>
          </Link>
        </Item>
        {isSuperAdmin(userRole) && can(permissions.AVERROES_USER) && (
          <Item className="sidebar__menu-item" key={AppPathNames.manageApplications}>
            <Link to={AppPathNames.manageApplications}>
              <AppstoreOutlined />
              <span>Applications</span>
            </Link>
          </Item>
        )}
        <Item className="sidebar__menu-item" key={AppPathNames.datasets} disabled={cannot(permissions.DATASETS_READ)}>
          <Link to={AppPathNames.datasets} disabled={cannot(permissions.DATASETS_READ)}>
            <DatabaseOutlined />
            <span>Datasets</span>
          </Link>
        </Item>
        {/* <Item className="sidebar__menu-item" key={AppPathNames.browseModels}>
          <Link to={AppPathNames.browseModels}>
            <BlockOutlined />
            <span>Models</span>
          </Link>
        </Item> */}

        {!isUiPathUser(userRole) && (
          <Item className="sidebar__menu-item" key={AppPathNames.browseFiles} disabled={cannot(permissions.FILES_READ)}>
            <Link to={AppPathNames.browseFiles} disabled={cannot(permissions.FILES_READ)}>
              <FolderAddOutlined />
              <span>Assets</span>
            </Link>
          </Item>
        )}
        <Item className="sidebar__menu-item" key={AppPathNames.machines}>
          <Link to={AppPathNames.machines}>
            <ControlOutlined />
            <span>Machines</span>
          </Link>
        </Item>
        {/* 
        {userRole === "uiPathUser" && (
          <Item className="sidebar__menu-item" key={AppPathNames.browseFiles}>
            <a
              href={"https://uipathlearn.averroes.ai/"}
              rel="noreferrer"
              target="_blank"
            >
              <FileOutlined />
              <span>Documentation</span>
            </a>
          </Item>
        )} */}
        {/* <Item className="sidebar__menu-item" key="/tools">
          <Link
            to={{
              pathname:
                "https://learn.averroes.ai/documentation/applications/application-cli",
            }}
            target="_blank"
          >
            <ToolOutlined />
            <span>Tools</span>
          </Link>
        </Item> */}
        <SubMenu
          className="sidebar__menu-item"
          key={AppPathNames.monitoring}
          disabled={!canAny([permissions.SCHEDULED_EXECUTION_READ])}
          title={
            <span
              style={{
                color: selectedKey === AppPathNames.monitoring ? "var(--color-primary)" : "#8C8C8C",
              }}
            >
              Monitoring
            </span>
          }
          icon={
            <Icon
              className="sidebar__monitor-icon"
              component={() => {
                let color = selectedKey === AppPathNames.monitoring ? "var(--color-primary)" : "#8C8C8C";
                return MonitorIcon(color);
              }}
            />
          }
        >
          {monitoringDropdownItems.map((item) => (
            <Item className="sidebar__menu-item" key={item.key} disabled={cannot(permissions.SCHEDULED_EXECUTION_READ)}>
              <Link to={item.key} disabled={cannot(permissions.SCHEDULED_EXECUTION_READ)}>
                <span>{item.text}</span>
              </Link>
            </Item>
          ))}
        </SubMenu>

        <Item
          className="sidebar__menu-item"
          key={AppPathNames.batchPredictions}
          disabled={cannot(permissions.EXECUTIONS_READ)}
        >
          <Link to={AppPathNames.batchPredictions} disabled={cannot(permissions.EXECUTIONS_READ)}>
            <ProfileOutlined />
            <span>Batch Predictions</span>
          </Link>
        </Item>
        <Item
          className="sidebar__menu-item"
          key={AppPathNames.connectors}
          disabled={cannot(permissions.CONNECTOR_READ)}
        >
          <Link to={AppPathNames.connectors} disabled={cannot(permissions.CONNECTOR_READ)}>
            <ApiOutlined />
            <span>Connectors</span>
          </Link>
        </Item>
        {/* <Item className="sidebar__menu-item" key={AppPathNames.ManageAccount}>
          <Link to={AppPathNames.ManageAccount}>
            <UserOutlined />
            <span>Manage Account</span>
          </Link>
        </Item> */}

        {/* <Item
          disabled
          className="sidebar__menu-item sidebar__menu-item--disabled" key={ProjectPaths.inviteUser}
        >
          <Link to={ProjectPaths.inviteUser}>
            <ShareAltOutlined />
            <span>Invite Users</span>
          </Link>
        </Item> */}
      </Menu>
    </Sider>
  );
};

export default React.memo(SideBar);
